import { Platform, type ViewStyle } from 'react-native';
import webTransition from '@utils/webTransition';

export const MENU_WIDTH_OPEN = 270;
export const MENU_WIDTH_COLLAPSED = 70;
/** Match ContextHandler rail timing so menu + dashboard offset stay in sync. */
export const MENU_TRANSFORM_MS = 200;
export const WEB_MENU_SHELL_MARGIN = 8;
export const WEB_MENU_SHELL_RADIUS = 16;

/** Shared web menu row metrics (PrimaryMenu + StaticMenuHeader). */
export const WEB_MENU_ROW = {
	iconCompact: 28,
	iconOpen: 36,
	rowMinHeight: 36,
	pressableMinHeight: 34,
	rowMarginVertical: 1,
	rowPaddingH: 4,
	rowPaddingV: 4,
	pressablePadding: 2,
	borderRadius: 6,
	listGapCollapsed: 2,
	listGapOpen: 4,
	/** Collapsed StaticMenuHeader — a bit tighter than PrimaryList listGapCollapsed */
	headerGapCollapsed: 1,
	/** StaticMenuHeader open rail — target chips per row on web */
	headerOpenColumns: 3,
	/** StaticMenuHeader collapsed rail — Lotties read smaller than primary mobileIcon SVGs */
	staticHeaderIconCompact: 40,
	staticHeaderRowCompact: 40,
	staticHeaderPressableCompact: 40,
} as const;

/** Offset for fixed web menu — dashboard content, footer, etc. */
export function getWebMenuContentOffset(menuOpen: boolean): number {
	return menuOpen ? MENU_WIDTH_OPEN : MENU_WIDTH_COLLAPSED;
}

export function getWebMenuBorderColor(theme: boolean): string {
	return theme ? '#e5e7eb' : '#374151';
}

/** Inset inside the fixed rail so the bordered shell sits neatly within the viewport. */
export function getWebMenuFixedFrameStyle(): ViewStyle {
	return {
		paddingTop: WEB_MENU_SHELL_MARGIN,
		paddingBottom: WEB_MENU_SHELL_MARGIN,
		paddingLeft: WEB_MENU_SHELL_MARGIN,
		paddingRight: WEB_MENU_SHELL_MARGIN,
		overflow: 'hidden',
		...(Platform.OS === 'web' ? { boxSizing: 'border-box' } : {}),
	};
}

/** Frosted bordered panel wrapping the full web menu (all four sides). */
export function getWebMenuBorderShellStyle(theme: boolean, extras?: ViewStyle): ViewStyle {
	const borderColor = getWebMenuBorderColor(theme);
	return {
		flex: 1,
		alignSelf: 'stretch',
		borderRadius: WEB_MENU_SHELL_RADIUS,
		borderWidth: 1,
		borderColor,
		backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
		overflow: 'hidden',
		minHeight: 0,
		minWidth: 0,
		maxWidth: '100%',
		...(Platform.OS === 'web'
			? {
					backdropFilter: 'blur(20px)',
					WebkitBackdropFilter: 'blur(20px)',
					boxSizing: 'border-box',
				}
			: {}),
		...(extras ?? {}),
	};
}

/** Shared frosted rail tokens (menu, messages panel, etc.) */
export const WEB_NAV_SHELL_MARGIN = WEB_MENU_SHELL_MARGIN;
export const WEB_NAV_SHELL_RADIUS = WEB_MENU_SHELL_RADIUS;
export const getWebNavBorderColor = getWebMenuBorderColor;
export const getWebNavFixedFrameStyle = getWebMenuFixedFrameStyle;
export const getWebNavBorderShellStyle = getWebMenuBorderShellStyle;

/** Usable width inside the frosted border shell (padding + 1px borders). */
export function getWebNavInnerContentWidth(outerWidth: number): number {
	if (Platform.OS !== 'web') return outerWidth;
	return Math.max(0, outerWidth - WEB_NAV_SHELL_MARGIN * 2 - 2);
}

/** StaticMenuHeader chip width for N-column grid (accounts for flex gap). */
export function getWebMenuHeaderOpenChipWidth(
	columns: number = WEB_MENU_ROW.headerOpenColumns,
	gapPx: number = WEB_MENU_ROW.listGapOpen,
): string {
	const totalGap = Math.max(0, columns - 1) * gapPx;
	return `calc((100% - ${totalGap}px) / ${columns})`;
}

/** Outer shell — fills the fixed frame; frame width is CSS-transitioned in Menu. */
export function getWebMenuShellStyle(
	menuOpen: boolean,
	extras?: ViewStyle,
): ViewStyle {
	if (Platform.OS === 'web') {
		return {
			width: '100%',
			maxWidth: '100%',
			minWidth: 0,
			overflow: 'hidden',
			flexShrink: 1,
			...(extras ?? {}),
		};
	}

	return {
		width: menuOpen ? MENU_WIDTH_OPEN : MENU_WIDTH_COLLAPSED,
		minWidth: MENU_WIDTH_COLLAPSED,
		overflow: 'hidden',
		flexShrink: 0,
		...(extras ?? {}),
	};
}

/** GPU/CSS width transition for the fixed web menu rail (same pattern as ContextHandler). */
export function getWebMenuRailTransitionStyle(): ViewStyle {
	return webTransition('width, max-width', `${MENU_TRANSFORM_MS}ms`, 'ease-in-out') as ViewStyle;
}

/** Inner frosted panel fills the shell. */
export function getWebMenuInnerStyle(
	_menuOpen: boolean,
	extras?: ViewStyle,
): ViewStyle {
	return {
		flex: 1,
		flexDirection: 'column',
		width: '100%',
		maxWidth: '100%',
		alignSelf: 'stretch',
		minHeight: 0,
		minWidth: 0,
		height: '100%',
		...(extras ?? {}),
	};
}

/** Full-width header band — centers the logo group (open + collapsed). */
export function getWebLogoRowStyle(_menuOpen: boolean): ViewStyle {
	return {
		alignSelf: 'stretch',
		width: '100%',
		minHeight: 60,
		maxHeight: 60,
		paddingTop: 0,
		paddingBottom: 0,
		paddingHorizontal: 8,
		overflow: 'hidden',
		position: 'relative',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	};
}

/** Logo + title cluster centered inside the header row. */
export function getWebLogoGroupStyle(menuOpen: boolean): ViewStyle {
	return {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		alignSelf: 'center',
		gap: menuOpen ? 8 : 0,
		flexShrink: 1,
		maxWidth: '100%',
		...webTransition('gap', `${MENU_TRANSFORM_MS}ms`, 'ease-in-out'),
	};
}

/** Open: beside logo in group. Collapsed: out of flow so only the logo shows, centered. */
export function getWebLogoTextStyle(menuOpen: boolean): ViewStyle {
	if (!menuOpen) {
		return {
			position: 'absolute',
			width: 0,
			height: 0,
			overflow: 'hidden',
			opacity: 0,
			pointerEvents: 'none',
		};
	}
	return {
		flexGrow: 0,
		flexShrink: 1,
		minWidth: 0,
		maxWidth: 160,
		overflow: 'hidden',
		alignItems: 'center',
		justifyContent: 'center',
	};
}

/** Between StaticMenuHeader quick actions and PrimaryList. */
export function getMenuSectionSeparatorStyle(
	theme: boolean,
	menuOpen: boolean,
): ViewStyle {
	return {
		alignSelf: 'stretch',
		height: 1,
		marginTop: menuOpen ? 8 : 6,
		marginBottom: menuOpen ? 8 : 6,
		marginHorizontal: menuOpen ? 12 : 10,
		backgroundColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.14)',
		flexShrink: 0,
	};
}

export function getWebMenuLabelStyle(
	menuOpen: boolean,
	theme: boolean,
): ViewStyle {
	return {
		flex: menuOpen ? 1 : 0,
		overflow: 'hidden',
		opacity: menuOpen ? 1 : 0,
		textAlign: 'center',
		color: theme ? '#333333' : 'white',
		fontWeight: '600' as const,
		...webTransition('opacity', `${MENU_TRANSFORM_MS}ms`, 'ease-in-out'),
	};
}
